import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link, usePage } from '@inertiajs/react'
import { ArrowRight, Copy, Loader2, SendHorizontal } from 'lucide-react'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Avatar as UserAvatar } from '@/components/design-system/Avatar'
import { Card, CardContent, CardHeader } from '@/components/ui/card'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Textarea } from '@/components/ui/textarea'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { agentHueStyle } from '@/frontend/lib/agentHue'
import { SimpleMarkdown } from '@/frontend/components/SimpleMarkdown'
import { PROVIDER_ICONS_SMALL, FallbackIcon } from '@/frontend/components/DigitalWorkers/providerIcons'
import {
  DiscordIcon,
  SlackIcon,
  WhatsAppIcon,
} from '@/frontend/components/DigitalWorkers/channelIcons'
import ComposioAppIcon from '@/frontend/components/Integrations/ComposioAppIcon'
import {
  PERSONALITY_DIMENSIONS,
  PROFILE_DESCRIPTIONS,
  PROFILE_LABELS,
  findDimensionForTrait,
  jobTitleForFleetProfile,
  parsePersonalityTraits,
} from '@/frontend/pages/Dashboard/DigitalWorkers/constants'
import {
  isComposioIntegration,
  type ComposioOrgIntegration,
  type FleetProfile,
  type GoogleAccountOption,
  type OrganizationContext,
  type OrgIntegration,
} from '@/frontend/pages/Dashboard/DigitalWorkers/types'
import { useAgentChatsChannel } from '@/frontend/pages/Dashboard/AgentChats/useAgentChatsChannel'
import {
  createSessionOverHttp,
  loadSessionOverHttp,
  sendMessageOverHttp,
} from '@/frontend/pages/Dashboard/AgentChats/api'
import type {
  AgentChatMessage,
  AgentChatSessionDetail,
  AgentChatSessionSummary,
} from '@/frontend/pages/Dashboard/AgentChats/types'
import type { SharedProps } from '@/frontend/types/inertia'
import { formatWhatsApp } from '@/frontend/utils/whatsappMask'

interface ChatMessage {
  id: string
  role: 'assistant' | 'user'
  content: string
  time: string
  pending?: boolean
}

interface Props {
  workerId: string
  workerName: string
  workerRole?: string | null
  workerProfile: FleetProfile
  workerPersonality?: string | null
  contactName?: string | null
  contactWhatsApp?: string | null
  workerAvatarUrl?: string | null
  integrations?: OrgIntegration[]
  organizationContext?: OrganizationContext | null
  googleAdsAccounts?: GoogleAccountOption[]
  googleAnalyticsProperties?: GoogleAccountOption[]
  metaAdAccounts?: GoogleAccountOption[]
  currentUserName?: string | null
}

const MAX_HEADER_INTEGRATION_ICONS = 4
const ONBOARDING_CHAT_SESSION_SOURCE = 'digital_worker_onboarding'
const ONBOARDING_INITIAL_PROMPT_SOURCE = 'digital_worker_onboarding_initial_prompt'

function formatTime(date = new Date()): string {
  return date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })
}

function formatMessageTime(value: string): string {
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? formatTime() : formatTime(date)
}

function messageFromPayload(message: AgentChatMessage): ChatMessage {
  return {
    id: message.id,
    role: message.role,
    content: message.content,
    time: formatMessageTime(message.created_at),
  }
}

function upsertChatMessage(messages: ChatMessage[], nextMessage: ChatMessage): ChatMessage[] {
  const existingIndex = messages.findIndex((message) => message.id === nextMessage.id)
  if (existingIndex === -1) return [...messages, nextMessage]

  return messages.map((message, index) => (
    index === existingIndex ? nextMessage : message
  ))
}

function upsertAgentMessage(messages: AgentChatMessage[], nextMessage: AgentChatMessage): AgentChatMessage[] {
  const existingIndex = messages.findIndex((message) => message.id === nextMessage.id)
  if (existingIndex === -1) return [...messages, nextMessage]

  return messages.map((message, index) => (
    index === existingIndex ? nextMessage : message
  ))
}

function isHiddenOnboardingInitialPrompt(message: AgentChatMessage): boolean {
  return message.role === 'user' && message.metadata?.source === ONBOARDING_INITIAL_PROMPT_SOURCE
}

function hasAssistantResponseFor(messages: AgentChatMessage[], userMessageId: string): boolean {
  return messages.some((message) => (
    message.role === 'assistant' && message.metadata?.in_reply_to_message_id === userMessageId
  ))
}

function patchSessionFromSummary(
  currentSession: AgentChatSessionDetail | null,
  summary: AgentChatSessionSummary,
): AgentChatSessionDetail | null {
  if (!currentSession || currentSession.id !== summary.id) return currentSession

  return {
    ...currentSession,
    ...summary,
    can_send: currentSession.can_send,
    messages: currentSession.messages,
  }
}

function firstDefined(value: string | null | undefined, fallback = 'Não informado'): string {
  return value?.trim() || fallback
}

function selectedAccountNames(accounts: GoogleAccountOption[]): string[] {
  return accounts
    .filter((account) => account.selected)
    .map((account) => account.name.trim())
    .filter(Boolean)
}

function joinList(values: string[]): string {
  if (values.length === 0) return 'Nenhum'
  return values.join(', ')
}

function personalityContext(personality: string | null | undefined): Record<string, string> {
  const traits = parsePersonalityTraits(personality)
  const byDimension = Object.fromEntries(
    PERSONALITY_DIMENSIONS.map((dimension) => [dimension.id, 'Não definido']),
  ) as Record<string, string>

  traits.forEach((trait) => {
    const dimension = findDimensionForTrait(trait)
    if (dimension) byDimension[dimension.id] = trait
  })

  return byDimension
}

function integrationsContext({
  integrations,
  googleAdsAccounts,
  googleAnalyticsProperties,
  metaAdAccounts,
}: {
  integrations: OrgIntegration[]
  googleAdsAccounts: GoogleAccountOption[]
  googleAnalyticsProperties: GoogleAccountOption[]
  metaAdAccounts: GoogleAccountOption[]
}): string {
  const activeIntegrations = integrations
    .filter((integration) => integration.status === 'active')
    .map((integration) => integration.provider_label)
    .filter(Boolean)

  const lines = [
    `- Integrações ativas no colaborador: ${joinList(activeIntegrations)}`,
    `- Contas Google Ads selecionadas: ${joinList(selectedAccountNames(googleAdsAccounts))}`,
    `- Propriedades Google Analytics selecionadas: ${joinList(selectedAccountNames(googleAnalyticsProperties))}`,
    `- Contas Meta selecionadas: ${joinList(selectedAccountNames(metaAdAccounts))}`,
  ]

  return lines.join('\n')
}

function initialAgentPrompt({
  workerName,
  workerRole,
  workerProfile,
  workerPersonality,
  contactName,
  contactWhatsApp,
  organizationContext,
  integrations,
  googleAdsAccounts,
  googleAnalyticsProperties,
  metaAdAccounts,
}: {
  workerName: string
  workerRole?: string | null
  workerProfile: FleetProfile
  workerPersonality?: string | null
  contactName?: string | null
  contactWhatsApp?: string | null
  organizationContext?: OrganizationContext | null
  integrations: OrgIntegration[]
  googleAdsAccounts: GoogleAccountOption[]
  googleAnalyticsProperties: GoogleAccountOption[]
  metaAdAccounts: GoogleAccountOption[]
}): string {
  const profileLabel = PROFILE_LABELS[workerProfile] ?? workerProfile
  const profileDescription = PROFILE_DESCRIPTIONS[workerProfile] ?? 'Não informado'
  const personality = personalityContext(workerPersonality)
  const formattedContactWhatsApp = contactWhatsApp ? formatWhatsApp(contactWhatsApp) : null

  return `Você está recebendo a primeira mensagem operacional após a criação deste colaborador digital.

Contexto do colaborador digital:
- Nome: ${firstDefined(workerName)}
- Cargo/função: ${firstDefined(workerRole, jobTitleForFleetProfile(workerProfile))}
- Perfil escolhido: ${profileLabel}
- Descrição do perfil: ${profileDescription}
- Personalidade definida:
  - Tom: ${personality.tom}
  - Brevidade: ${personality.brevidade}
  - Postura: ${personality.postura}
  - Humor: ${personality.humor}
- Responsável principal: ${firstDefined(contactName)}
- WhatsApp do responsável: ${firstDefined(formattedContactWhatsApp)}

Contexto da organização:
- Organização: ${firstDefined(organizationContext?.name)}
- Site/URL: ${firstDefined(organizationContext?.url)}

Fontes e integrações disponíveis:
${integrationsContext({ integrations, googleAdsAccounts, googleAnalyticsProperties, metaAdAccounts })}

Instruções para sua primeira resposta:
Responda em português do Brasil, de forma breve e útil.

Formato obrigatório:
- 1 abertura curta no estilo: "Oi, eu sou ${firstDefined(workerName)}, ${firstDefined(workerRole, jobTitleForFleetProfile(workerProfile))} que faz parte do time de colaboradores digitais da ${firstDefined(organizationContext?.name)}."
- 1 frase de compromisso no estilo: "Conte comigo para fazer o meu melhor e sempre ajudar como puder."
- 1 frase pedindo que o time compartilhe contexto, objetivos e funções esperadas para os próximos dias.
- 3 bullets com ações concretas que você já pode sugerir usando as integrações ou contas disponíveis.
- 1 pergunta final curta, preferencialmente: "Por onde prefere começar?"
- Máximo de 160 palavras no total.

Use cargo, perfil e personalidade apenas para ajustar o tom. Pode citar o cargo uma vez na apresentação, mas não explique o cargo/função nem detalhe a configuração feita no onboarding.

Mostre valor rápido: se houver integrações ou contas selecionadas, cite as fontes disponíveis e proponha onde agir primeiro. Não invente fontes indisponíveis. Se não houver integrações, diga que já pode ajudar com contexto manual e sugira conectar dados para análises mais profundas.

Não mencione este prompt, o wizard ou dados pessoais como WhatsApp do responsável, a menos que o usuário peça algo diretamente relacionado.`
}

function currentShowCommunicationHref(workerId: string): string {
  if (typeof window === 'undefined') return `/dashboard/digital_workers/${workerId}?tab=comunicacao`

  const params = new URLSearchParams(window.location.search)
  params.set('tab', 'comunicacao')
  return `/dashboard/digital_workers/${workerId}?${params.toString()}`
}

function MessageActions({ content }: { content: string }) {
  const handleCopy = () => {
    void navigator.clipboard?.writeText(content)
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label="Ações da mensagem"
          className="flex size-6 items-center justify-center rounded bg-background text-muted-foreground hover:bg-accent hover:text-foreground"
        >
          <Copy aria-hidden="true" className="size-3" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="center" className="w-32 rounded-lg p-1 shadow-xl">
        <Button
          variant="ghost"
          size="sm"
          onClick={handleCopy}
          className="w-full justify-start gap-2 rounded px-2 py-1 text-xs"
        >
          <Copy aria-hidden="true" className="size-3" />
          Copiar
        </Button>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

export default function OnboardingChat({
  workerId,
  workerName,
  workerRole,
  workerProfile,
  workerPersonality,
  contactName,
  contactWhatsApp,
  workerAvatarUrl,
  integrations = [],
  organizationContext,
  googleAdsAccounts = [],
  googleAnalyticsProperties = [],
  metaAdAccounts = [],
  currentUserName,
}: Props) {
  const { auth } = usePage<SharedProps>().props
  const organizationId = auth.user?.active_organization?.id ?? null
  const visibleIntegrations = integrations.slice(0, MAX_HEADER_INTEGRATION_ICONS)
  const extraIntegrations = Math.max(0, integrations.length - MAX_HEADER_INTEGRATION_ICONS)
  const trimmedUserName = currentUserName?.trim() || ''
  const communicationHref = currentShowCommunicationHref(workerId)
  const initialPrompt = useMemo(() => initialAgentPrompt({
    workerName,
    workerRole,
    workerProfile,
    workerPersonality,
    contactName,
    contactWhatsApp,
    organizationContext,
    integrations,
    googleAdsAccounts,
    googleAnalyticsProperties,
    metaAdAccounts,
  }), [
    contactName,
    contactWhatsApp,
    googleAdsAccounts,
    googleAnalyticsProperties,
    integrations,
    metaAdAccounts,
    organizationContext,
    workerName,
    workerPersonality,
    workerProfile,
    workerRole,
  ])
  const activeSessionIdRef = useRef<string | null>(null)
  const pendingDraftRef = useRef<string | null>(null)
  const autoStartRequestedRef = useRef(false)
  const wasDisconnectedRef = useRef(false)

  const [session, setSession] = useState<AgentChatSessionDetail | null>(null)
  const [messages, setMessages] = useState<ChatMessage[]>([])
  const [pendingOutbound, setPendingOutbound] = useState<ChatMessage | null>(null)
  const [draft, setDraft] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)
  const messagesEndRef = useRef<HTMLDivElement>(null)

  const acceptSessionDetail = useCallback((nextSession: AgentChatSessionDetail) => {
    activeSessionIdRef.current = nextSession.id
    pendingDraftRef.current = null
    setSession(nextSession)
    setMessages(nextSession.messages
      .filter((message) => !isHiddenOnboardingInitialPrompt(message))
      .map((message) => messageFromPayload(message)))
    setPendingOutbound(null)
    setSubmitting(false)
    setSubmitError(null)
  }, [])

  const { connectionStatus, startSession, loadSession, sendMessage } = useAgentChatsChannel({
    organizationId,
    onSessionUpsert: (nextSession) => {
      if (activeSessionIdRef.current !== nextSession.id) return
      setSession((currentSession) => patchSessionFromSummary(currentSession, nextSession))
    },
    onSessionStarted: acceptSessionDetail,
    onSessionLoaded: (nextSession) => {
      if (activeSessionIdRef.current && activeSessionIdRef.current !== nextSession.id) return
      acceptSessionDetail(nextSession)
    },
    onMessageCreated: (sessionId, message) => {
      if (activeSessionIdRef.current !== sessionId) return

      if (message.role === 'user') {
        pendingDraftRef.current = null
        setPendingOutbound(null)
        setSubmitting(false)
      }

      if (!isHiddenOnboardingInitialPrompt(message)) {
        setMessages((currentMessages) => upsertChatMessage(currentMessages, messageFromPayload(message)))
      }
      setSession((currentSession) => {
        if (!currentSession || currentSession.id !== sessionId) return currentSession

        const nextMessages = upsertAgentMessage(currentSession.messages, message)
        return {
          ...currentSession,
          awaiting_response: message.role === 'assistant' ? false : currentSession.awaiting_response,
          pending_turn: message.role === 'assistant' ? null : currentSession.pending_turn,
          has_visible_user_message:
            currentSession.has_visible_user_message ||
            (message.role === 'user' && !isHiddenOnboardingInitialPrompt(message)),
          last_message_at: message.created_at,
          message_count: nextMessages.length,
          messages: nextMessages,
          response_error: message.role === 'assistant' ? null : currentSession.response_error,
        }
      })
    },
    onMessageSubmitted: (sessionId, messageId) => {
      if (activeSessionIdRef.current !== sessionId) return

      setSubmitting(false)
      setSession((currentSession) => {
        if (!currentSession || currentSession.id !== sessionId) return currentSession

        return {
          ...currentSession,
          awaiting_response: !hasAssistantResponseFor(currentSession.messages, messageId),
          pending_turn: hasAssistantResponseFor(currentSession.messages, messageId) ? null : currentSession.pending_turn,
          response_error: null,
        }
      })
    },
    onSubmissionFailed: (sessionId, error) => {
      if (sessionId && activeSessionIdRef.current && activeSessionIdRef.current !== sessionId) return

      const pendingDraft = pendingDraftRef.current
      pendingDraftRef.current = null
      setPendingOutbound(null)
      setSubmitting(false)
      setSubmitError(error)
      if (pendingDraft) setDraft(pendingDraft)
    },
  })

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' })
  }, [messages.length, pendingOutbound, session?.awaiting_response])

  useEffect(() => {
    activeSessionIdRef.current = session?.id ?? null
  }, [session?.id])

  useEffect(() => {
    if (connectionStatus === 'disconnected') {
      wasDisconnectedRef.current = true
      return
    }

    if (connectionStatus === 'connected' && wasDisconnectedRef.current && activeSessionIdRef.current) {
      loadSession(activeSessionIdRef.current)
      wasDisconnectedRef.current = false
    }
  }, [connectionStatus, loadSession])

  useEffect(() => {
    if (!session?.awaiting_response) return undefined

    const intervalId = window.setInterval(() => {
      if (connectionStatus === 'connected') {
        loadSession(session.id)
        return
      }

      loadSessionOverHttp(session.id)
        .then(acceptSessionDetail)
        .catch((error: unknown) => {
          setSubmitError(error instanceof Error ? error.message : 'Não foi possível sincronizar a conversa agora.')
        })
    }, 1000)

    return () => window.clearInterval(intervalId)
  }, [acceptSessionDetail, connectionStatus, loadSession, session?.awaiting_response, session?.id])

  const submitMessage = useCallback(async (
    content: string,
    options?: { forceHttp?: boolean; hiddenPending?: boolean; source?: typeof ONBOARDING_CHAT_SESSION_SOURCE },
  ) => {
    const trimmed = content.trim()
    if (!trimmed || submitting || session?.awaiting_response) return

    setSubmitError(null)
    const canUseCable = connectionStatus === 'connected' && !options?.forceHttp
    if (!canUseCable && !session && !options?.source) return

    const pendingMessage = options?.hiddenPending ? null : {
      id: `pending-${Date.now()}`,
      role: 'user' as const,
      content: trimmed,
      time: formatTime(),
      pending: true,
    }

    pendingDraftRef.current = options?.hiddenPending ? null : trimmed
    setSubmitting(true)
    setPendingOutbound(pendingMessage)
    setDraft('')

    try {
      if (session) {
        if (canUseCable && sendMessage(session.id, trimmed)) return

        acceptSessionDetail(await sendMessageOverHttp(session.id, trimmed))
        return
      }

      if (canUseCable && startSession(workerId, trimmed, undefined, options?.source)) return

      acceptSessionDetail(await createSessionOverHttp({
        workerId,
        content: trimmed,
        source: options?.source,
      }))
    } catch (error) {
      const pendingDraft = pendingDraftRef.current
      pendingDraftRef.current = null
      setPendingOutbound(null)
      if (pendingDraft) setDraft(pendingDraft)
      setSubmitting(false)
      setSubmitError(error instanceof Error ? error.message : 'Não foi possível enviar a mensagem agora.')
    }
  }, [acceptSessionDetail, connectionStatus, sendMessage, session, startSession, submitting, workerId])

  useEffect(() => {
    if (session || autoStartRequestedRef.current) return

    if (connectionStatus === 'connected') {
      autoStartRequestedRef.current = true
      void submitMessage(initialPrompt, {
        hiddenPending: true,
        source: ONBOARDING_CHAT_SESSION_SOURCE,
      })
      const timeoutId = window.setTimeout(() => {
        if (activeSessionIdRef.current) return

        void submitMessage(initialPrompt, {
          forceHttp: true,
          hiddenPending: true,
          source: ONBOARDING_CHAT_SESSION_SOURCE,
        })
      }, 3000)

      return () => window.clearTimeout(timeoutId)
    }

    const timeoutId = window.setTimeout(() => {
      if (autoStartRequestedRef.current || activeSessionIdRef.current) return

      autoStartRequestedRef.current = true
      void submitMessage(initialPrompt, {
        forceHttp: true,
        hiddenPending: true,
        source: ONBOARDING_CHAT_SESSION_SOURCE,
      })
    }, 1500)

    return () => window.clearTimeout(timeoutId)
  }, [connectionStatus, initialPrompt, session, submitMessage])

  const send = () => {
    submitMessage(draft)
  }

  const handleKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault()
      send()
    }
  }

  const workerInitial = workerName.trim().charAt(0).toUpperCase() || 'A'
  // Stable identity hue (B.4): same seed (worker id) as the rest of the app, so
  // the agent already "owns" its color from the very first conversation.
  const workerHueStyle = agentHueStyle(workerId)
  const visibleMessages = pendingOutbound ? [...messages, pendingOutbound] : messages
  const responsePending = Boolean(session?.awaiting_response || (submitting && !pendingOutbound))
  const responsePendingLabel = `${workerName} está digitando…`
  const inputDisabled = submitting || session?.awaiting_response || (!session && connectionStatus !== 'connected')
  const visibleSubmitError = submitError || session?.response_error
  const inputPlaceholder = connectionStatus === 'connected' || session
    ? `Mande uma mensagem para ${workerName}…`
    : 'Preparando a conversa…'

  return (
    <div className="mx-auto flex w-full max-w-2xl flex-col gap-3">
    <Card
      className="flex h-[75vh] min-h-0 flex-col gap-0 overflow-hidden py-0 shadow-none"
      data-testid="onboarding-chat"
    >
      <CardHeader className="flex shrink-0 flex-row items-center justify-between gap-3 space-y-0 border-b border-border bg-background px-4 !py-2">
        <div className="flex min-w-0 items-center gap-3">
          <div className="relative shrink-0">
            <Avatar>
              {workerAvatarUrl ? <AvatarImage alt="" src={workerAvatarUrl} /> : null}
              <AvatarFallback className="bg-agent text-agent-foreground" style={workerHueStyle}>
                {workerInitial}
              </AvatarFallback>
            </Avatar>
            <span
              aria-hidden="true"
              className="pointer-events-none absolute -bottom-0.5 -right-0.5 flex h-3 w-3"
            >
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-green-500 opacity-60" />
              <span className="relative inline-flex h-3 w-3 rounded-full bg-green-500 ring-2 ring-background" />
            </span>
            <span className="sr-only">Online</span>
          </div>
          <div className="min-w-0">
            <p className="truncate text-base font-semibold text-foreground">{workerName}</p>
            {workerRole && <p className="truncate text-xs text-muted-foreground">{workerRole}</p>}
          </div>
        </div>

        {visibleIntegrations.length > 0 && (
          <div
            className="flex items-center -space-x-1.5"
            data-testid="onboarding-chat-integrations"
            aria-label={`${integrations.length} integrações conectadas`}
          >
            {visibleIntegrations.map((integration) => (
              <span
                key={integration.id}
                title={integration.provider_label}
                className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full border-2 border-card bg-background shadow-sm"
              >
                {isComposioIntegration(integration) ? (
                  <ComposioAppIcon
                    logo={(integration as ComposioOrgIntegration).logo}
                    name={integration.provider_label}
                    className="h-3.5 w-3.5"
                  />
                ) : (
                  PROVIDER_ICONS_SMALL[integration.provider] || <FallbackIcon />
                )}
              </span>
            ))}
            {extraIntegrations > 0 && (
              <span className="flex h-7 min-w-7 items-center justify-center rounded-full border-2 border-card bg-background px-1.5 text-[10px] font-semibold text-muted-foreground shadow-sm">
                +{extraIntegrations}
              </span>
            )}
          </div>
        )}
      </CardHeader>

      <CardContent className="min-h-0 flex-1 p-0">
        <ScrollArea
          aria-label="Conversa com o colaborador"
          role="log"
          className="h-full bg-background"
        >
          <div className="flex flex-col gap-1.5 p-4">
            {visibleMessages.map((msg) => {
              const isMe = msg.role === 'user'
              return (
                <div
                  key={msg.id}
                  className={cn('group flex gap-2', isMe ? 'justify-end' : 'justify-start')}
                >
                  <div
                    className={cn(
                      'flex max-w-[80%] items-start gap-2',
                      isMe && 'flex-row-reverse',
                    )}
                  >
                    {!isMe ? (
                      <Avatar className="size-8 shrink-0">
                        {workerAvatarUrl ? <AvatarImage alt="" src={workerAvatarUrl} /> : null}
                        <AvatarFallback className="bg-agent text-agent-foreground" style={workerHueStyle}>
                          {workerInitial}
                        </AvatarFallback>
                      </Avatar>
                    ) : trimmedUserName ? (
                      <UserAvatar name={trimmedUserName} size="sm" className="shrink-0" />
                    ) : null}
                    <div className={cn('flex flex-col', isMe && 'items-end')}>
                      <div
                        className={cn(
                          'rounded-md px-3 py-2 text-sm leading-relaxed',
                          isMe
                            ? 'bg-primary text-primary-foreground'
                            : 'bg-accent text-foreground',
                          msg.pending && 'opacity-70',
                        )}
                      >
                        {isMe ? msg.content : (
                          <SimpleMarkdown
                            content={msg.content}
                            allowImages={false}
                            className="break-words [&_*:first-child]:mt-0 [&_*:last-child]:mb-0"
                          />
                        )}
                      </div>
                      <div className="mt-0.5 flex items-center gap-2">
                        <time className="text-[10px] text-muted-foreground" dateTime={msg.time}>
                          {msg.time}
                        </time>
                        <div className="opacity-0 transition-opacity group-hover:opacity-100">
                          <MessageActions content={msg.content} />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )
            })}
            {responsePending && (
              <div className="flex justify-start gap-2" aria-live="polite">
                <div className="flex max-w-[80%] items-start gap-2">
                  <Avatar className="size-8 shrink-0">
                    {workerAvatarUrl ? <AvatarImage alt="" src={workerAvatarUrl} /> : null}
                    <AvatarFallback className="bg-agent text-agent-foreground" style={workerHueStyle}>
                      {workerInitial}
                    </AvatarFallback>
                  </Avatar>
                  <div className="rounded-md bg-accent px-3 py-2 text-sm text-muted-foreground">
                    <span className="inline-flex items-center gap-2">
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      {responsePendingLabel}
                    </span>
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>
        </ScrollArea>
      </CardContent>

      <div className="relative shrink-0 p-2">
        <Textarea
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          onKeyDown={handleKeyDown}
          disabled={inputDisabled}
          rows={2}
          placeholder={inputPlaceholder}
          aria-label={`Mensagem para ${workerName}`}
          className="max-h-40 min-h-[44px] resize-none overflow-y-auto rounded-2xl border-border bg-card py-3 pr-14 pl-4 shadow-sm focus-visible:ring-2 focus-visible:ring-violet-500/30"
        />
        <button
          type="button"
          onClick={send}
          disabled={!draft.trim() || inputDisabled}
          aria-label="Enviar mensagem"
          className="absolute bottom-4 right-4 flex h-8 w-8 items-center justify-center rounded-full bg-foreground text-background transition-all hover:bg-foreground/90 hover:scale-105 disabled:cursor-not-allowed disabled:opacity-30 disabled:hover:scale-100"
        >
          {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <SendHorizontal className="h-4 w-4" strokeWidth={2.5} />}
        </button>
      </div>
    </Card>

    {visibleSubmitError && (
      <p className="text-center text-sm text-destructive" role="alert">{visibleSubmitError}</p>
    )}

    <div className="flex justify-center">
      <Link
        href={communicationHref}
        data-testid="onboarding-chat-channels-cta"
        className="group inline-flex items-center gap-2 rounded-full border border-border bg-card px-3 py-1.5 shadow-sm transition-all hover:-translate-y-[1px] hover:border-violet-400/60 hover:shadow-md"
      >
        <span className="flex items-center -space-x-1">
          <span className="flex size-5 items-center justify-center rounded-full border border-card bg-background">
            <WhatsAppIcon />
          </span>
          <span className="flex size-5 items-center justify-center rounded-full border border-card bg-background">
            <SlackIcon />
          </span>
          <span className="flex size-5 items-center justify-center rounded-full border border-card bg-background">
            <DiscordIcon />
          </span>
        </span>
        <span className="text-xs font-medium text-foreground">
          Integre {workerName} com seus canais de comunicação
        </span>
        <ArrowRight className="h-3 w-3 shrink-0 text-muted-foreground transition-transform group-hover:translate-x-0.5" />
      </Link>
    </div>
    </div>
  )
}
